/**
 * Trash.tsx — the papelera (soft-deleted assets).
 *
 * Lists assets with `trashedAt` set (`api.assetQueries.listTrashedAssets`) and
 * offers per-row "Restaurar" (`api.assetQueries.restoreFromTrash`) plus
 * "Eliminar permanentemente" (`api.mutations.assets.deleteAsset`). Permanent
 * delete removes the DB row AND purges the S3 derivatives (`assets/<id>/` +
 * `transcoded/<id>/`) via the scheduled `purgeAssetStorage` action — the
 * invariant in docs/remediations/2026-06-15-asset-purge.md: removing the
 * registry always removes the S3 objects. It is irreversible, so it is gated
 * behind a typed-confirm ConfirmModal.
 *
 * Honest-state: `undefined` → skeleton; `[]` → empty state; backend throws
 * surface as an error toast via useMutationWithToast (FR-013 H1).
 */
import { useState } from 'react';
import { api } from '@convex/api';
import { useQuery } from 'convex/react';
import {
  Btn, Panel, PageHead, EmptyState, SkeletonList, Pill, ConfirmModal,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { fmtDur, ago } from '../../lib/format';
import { navigate } from '../../lib/nav';

interface TrashedRow {
  _id: string;
  title: string;
  assetType: string;
  transcodingStatus: string;
  durationMs?: number;
  thumbnailUrl?: string;
  trashedAt?: number;
}

export function Trash() {
  const rows = useQuery(api.assetQueries.listTrashedAssets, {}) as
    | TrashedRow[]
    | undefined;

  const restoreM = useMutationWithToast(api.assetQueries.restoreFromTrash, {
    logLabel: 'assets_restoreFromTrash',
    successMessage: 'Restaurado desde la papelera.',
    errorMessagePrefix: 'No se pudo restaurar',
  });

  const deleteM = useMutationWithToast(api.mutations.assets.deleteAsset, {
    logLabel: 'assets_deleteAsset',
    successMessage: 'Eliminación iniciada — purgando archivos en S3…',
    errorMessagePrefix: 'No se pudo eliminar',
  });

  // Rows pending permanent deletion (drives the typed-confirm modal). One entry
  // for a single-row delete, many for a bulk delete from the selection.
  const [pendingDelete, setPendingDelete] = useState<TrashedRow[] | null>(null);

  // Multi-select state: the set of selected row ids.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const busy = restoreM.loading || deleteM.loading;

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Run a mutation over every selected row, then clear the selection. Each call
  // surfaces its own toast (honest per-item success/failure); allSettled means
  // one failure never aborts the rest.
  const runBulk = async (
    targets: TrashedRow[],
    m: typeof restoreM,
  ) => {
    await Promise.allSettled(
      targets.map((t) => m.run({ assetId: t._id as any })),
    );
    setSelected(new Set());
  };

  const header = (
    <PageHead
      crumbs={[{ label: 'Contenido', href: '#/library' }]}
      title="Papelera"
      description="Videos movidos a la papelera. Restaúralos para devolverlos a la biblioteca."
      actions={<Btn variant="ghost" icon="chevronL" onClick={() => navigate('/library')}>Volver a la biblioteca</Btn>}
    />
  );

  if (rows === undefined) {
    return (
      <div>
        {header}
        <SkeletonList rows={6} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div>
        {header}
        <Panel>
          <EmptyState
            icon="trash"
            title="La papelera está vacía"
            body="Los videos que muevas a la papelera aparecerán aquí y podrás restaurarlos."
          />
        </Panel>
      </div>
    );
  }

  // Rows already being purged can't be acted on — exclude them from selection.
  const selectableRows = rows.filter((r) => r.transcodingStatus !== 'removing');
  const selectedRows = selectableRows.filter((r) => selected.has(r._id));
  const allSelected =
    selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableRows.map((r) => r._id)));

  return (
    <div>
      {header}
      <Panel>
        {/* Selection toolbar: select-all + bulk actions over the selection. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '4px 8px 12px',
            borderBottom: '1px solid var(--hairline)',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--body-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width: 16, height: 16, cursor: 'pointer' }} />
            {selectedRows.length > 0 ? `${selectedRows.length} seleccionado(s)` : 'Seleccionar todo'}
          </label>
          {selectedRows.length > 0 && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Btn
                variant="ghost"
                icon="refresh"
                disabled={busy}
                onClick={() => { void runBulk(selectedRows, restoreM); }}
              >
                Restaurar ({selectedRows.length})
              </Btn>
              <Btn
                variant="danger"
                icon="trash"
                disabled={busy}
                onClick={() => setPendingDelete(selectedRows)}
              >
                Eliminar ({selectedRows.length})
              </Btn>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((a) => {
            const isRemoving = a.transcodingStatus === 'removing';
            const isFailed = a.transcodingStatus === 'remove_failed';
            return (
            <div
              key={a._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '12px 8px',
                borderBottom: '1px solid var(--hairline)',
                background: selected.has(a._id) ? 'var(--lightest-gray)' : undefined,
                opacity: isRemoving ? 0.6 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(a._id)}
                onChange={() => toggleRow(a._id)}
                disabled={isRemoving}
                style={{ width: 16, height: 16, flexShrink: 0, cursor: isRemoving ? 'not-allowed' : 'pointer' }}
              />
              <div
                style={{
                  width: 84,
                  height: 47,
                  borderRadius: 6,
                  flexShrink: 0,
                  background: a.thumbnailUrl ? `center / cover no-repeat url(${a.thumbnailUrl})` : 'var(--hairline)',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</div>
                <div style={{ fontSize: 12, color: 'var(--body-muted)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Pill tone="neutral">{a.assetType}</Pill>
                  {isRemoving && <Pill tone="warn" dot>Eliminando…</Pill>}
                  {isFailed && <Pill tone="bad">Error al eliminar S3</Pill>}
                  {a.durationMs ? <span>{fmtDur(a.durationMs)}</span> : null}
                  {a.trashedAt ? <span>En papelera {ago(a.trashedAt)}</span> : null}
                </div>
              </div>
              {isRemoving ? (
                <span style={{ fontSize: 12, color: 'var(--body-muted)' }}>Purgando S3…</span>
              ) : (
                <>
              <Btn
                variant="ghost"
                icon="refresh"
                disabled={busy}
                onClick={() => { void restoreM.run({ assetId: a._id as any }).catch(() => {}); }}
              >
                Restaurar
              </Btn>
              <Btn
                variant="danger"
                icon={isFailed ? 'refresh' : 'trash'}
                disabled={busy}
                onClick={() => setPendingDelete([a])}
              >
                {isFailed ? 'Reintentar' : 'Eliminar'}
              </Btn>
                </>
              )}
            </div>
            );
          })}
        </div>
      </Panel>

      <ConfirmModal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Eliminar permanentemente"
        danger
        confirmLabel="Eliminar para siempre"
        typed="ELIMINAR"
        body={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0 }}>
              {pendingDelete && pendingDelete.length === 1 ? (
                <>Se eliminará <strong>«{pendingDelete[0].title}»</strong></>
              ) : (
                <>Se eliminarán <strong>{pendingDelete?.length} videos</strong></>
              )}
              {' '}de forma permanente: el registro y{' '}
              <strong>todos sus archivos en S3</strong> (fuente original y HLS
              transcodificado). Esta acción no se puede deshacer.
            </p>
            <p style={{ margin: 0, color: 'var(--body-muted)', fontSize: 13 }}>
              Escribe <strong>ELIMINAR</strong> para confirmar.
            </p>
          </div>
        }
        onConfirm={() => {
          const targets = pendingDelete;
          setPendingDelete(null);
          if (!targets || targets.length === 0) return;
          void runBulk(targets, deleteM);
        }}
      />
    </div>
  );
}

export default Trash;
